import { ReactNode } from 'react';
import { basketballStats } from '@/mocks/estadisticas';

type Team = typeof basketballStats[0];

interface BasketballTableProps {
  data: Team[];
  selectedFilter: string;
  selectedId?: number;
  onSelect?: (team: Team) => void;
}

interface ColDef {
  key: string;
  header: string;
  highlight: boolean;
  cell: (t: Team) => ReactNode;
}

function FormBadge({ r }: { r: string }) {
  const cls = r === 'W' ? 'bg-[#00e87a]/20 text-[#00e87a]' : r === 'L' ? 'bg-[#ef4444]/20 text-[#ef4444]' : 'bg-[#f59e0b]/20 text-[#f59e0b]';
  return <span className={`w-5 h-5 flex items-center justify-center rounded text-xs font-black ${cls}`}>{r}</span>;
}

function Pill({ value, green, yellow }: { value: string | number; green: boolean; yellow?: boolean }) {
  const cls = green ? 'bg-[#00e87a]/15 text-[#00e87a]' : yellow ? 'bg-[#f59e0b]/15 text-[#f59e0b]' : 'bg-white/5 text-[#a8b2c1]';
  return <span className={`px-2 py-0.5 rounded text-xs font-bold ${cls}`}>{value}</span>;
}

const STAT_COLS: Record<string, ColDef[]> = {
  scoring: [
    {
      key: 'pts', header: 'Pts/P', highlight: true,
      cell: (t) => <span className={`font-bold text-sm ${t.ptsFor >= 118 ? 'text-[#00e87a]' : 'text-white'}`}>{t.ptsFor}</span>,
    },
    { key: 'pta', header: 'Pts Rival', highlight: false, cell: (t) => <span className="text-[#6b7280] text-sm tabular-nums">{t.ptsAgainst}</span> },
    {
      key: 'pm', header: '+/-', highlight: false,
      cell: (t) => <span className={`font-bold text-sm tabular-nums ${t.plusMinus > 0 ? 'text-[#00e87a]' : 'text-[#ef4444]'}`}>{t.plusMinus > 0 ? '+' : ''}{t.plusMinus}</span>,
    },
  ],
  rebounds: [
    {
      key: 'reb', header: 'Reb/P', highlight: true,
      cell: (t) => <span className={`font-bold text-sm ${t.rebPerGame > 43 ? 'text-[#00e87a]' : 'text-white'}`}>{t.rebPerGame}</span>,
    },
    { key: 'rebo', header: 'Reb Of.', highlight: false, cell: (t) => <span className="text-[#a8b2c1] text-sm tabular-nums">{t.rebOff}</span> },
    { key: 'rebd', header: 'Reb Def.', highlight: false, cell: (t) => <span className="text-[#a8b2c1] text-sm tabular-nums">{t.rebDef}</span> },
    { key: 'pts', header: 'Pts/P', highlight: false, cell: (t) => <span className="text-[#a8b2c1] text-sm tabular-nums">{t.ptsFor}</span> },
  ],
  assists: [
    {
      key: 'ast', header: 'Ast/P', highlight: true,
      cell: (t) => <span className={`font-bold text-sm ${t.astPerGame > 25 ? 'text-[#00e87a]' : 'text-white'}`}>{t.astPerGame}</span>,
    },
    { key: 'pts', header: 'Pts/P', highlight: false, cell: (t) => <span className="text-[#a8b2c1] text-sm tabular-nums">{t.ptsFor}</span> },
    { key: 'fg', header: 'FG%', highlight: false, cell: (t) => <Pill value={`${t.fgPct}%`} green={t.fgPct >= 50} yellow={t.fgPct >= 47 && t.fgPct < 50} /> },
  ],
  shooting: [
    {
      key: 'fg', header: 'FG%', highlight: true,
      cell: (t) => <span className={`font-bold text-sm ${t.fgPct >= 50 ? 'text-[#00e87a]' : 'text-white'}`}>{t.fgPct}%</span>,
    },
    { key: '3p', header: '3P%', highlight: false, cell: (t) => <Pill value={`${t.threePct}%`} green={t.threePct >= 39} yellow={t.threePct >= 36 && t.threePct < 39} /> },
    { key: 'pts', header: 'Pts/P', highlight: false, cell: (t) => <span className="text-[#a8b2c1] text-sm tabular-nums">{t.ptsFor}</span> },
    { key: 'ast', header: 'Ast/P', highlight: false, cell: (t) => <span className="text-[#a8b2c1] text-sm tabular-nums">{t.astPerGame}</span> },
  ],
  defense: [
    {
      key: 'pta', header: 'Pts Rival', highlight: true,
      cell: (t) => <span className={`font-bold text-sm ${t.ptsAgainst < 108 ? 'text-[#00e87a]' : t.ptsAgainst < 112 ? 'text-[#f59e0b]' : 'text-white'}`}>{t.ptsAgainst}</span>,
    },
    {
      key: 'pm', header: '+/-', highlight: false,
      cell: (t) => <span className={`font-bold text-sm tabular-nums ${t.plusMinus > 0 ? 'text-[#00e87a]' : 'text-[#ef4444]'}`}>{t.plusMinus > 0 ? '+' : ''}{t.plusMinus}</span>,
    },
    { key: 'reb', header: 'Reb/P', highlight: false, cell: (t) => <span className="text-[#a8b2c1] text-sm tabular-nums">{t.rebPerGame}</span> },
    { key: 'fg', header: 'FG% rival', highlight: false, cell: (t) => <span className="text-[#6b7280] text-xs">—</span> },
  ],
};

const SORT_FN: Record<string, (a: Team, b: Team) => number> = {
  scoring: (a, b) => b.ptsFor - a.ptsFor,
  rebounds: (a, b) => b.rebPerGame - a.rebPerGame,
  assists: (a, b) => b.astPerGame - a.astPerGame,
  shooting: (a, b) => b.fgPct - a.fgPct,
  defense: (a, b) => a.ptsAgainst - b.ptsAgainst, // ascending (lower pts against = better)
};

export default function BasketballTable({ data, selectedFilter, selectedId, onSelect }: BasketballTableProps) {
  const cols = STAT_COLS[selectedFilter] ?? STAT_COLS.scoring;
  const sortFn = SORT_FN[selectedFilter] ?? SORT_FN.scoring;
  const sorted = [...data].sort(sortFn);

  return (
    <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[#2a2d3a] flex items-center justify-between">
        <h3 className="text-white font-bold text-sm">
          Estadísticas de Equipos — Baloncesto
          <span className="ml-2 px-2 py-0.5 rounded-md bg-[#00e87a]/10 text-[#00e87a] text-xs font-bold">
            {cols.find((c) => c.highlight)?.header}
          </span>
        </h3>
        <span className="text-[#6b7280] text-xs">{sorted.length} equipos</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr className="border-b border-[#2a2d3a]">
              <th className="px-5 py-3 text-left text-[#6b7280] text-xs font-semibold uppercase tracking-wide">Equipo</th>
              <th className="px-4 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase">PJ</th>
              {cols.map((c) => (
                <th
                  key={c.key}
                  className={`px-4 py-3 text-center text-xs font-semibold uppercase ${c.highlight ? 'text-[#00e87a]' : 'text-[#6b7280]'}`}
                >
                  {c.header}
                </th>
              ))}
              <th className="px-4 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase">Forma</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((t, i) => (
              <tr
                key={t.id}
                onClick={() => onSelect?.(t)}
                className={`border-b border-[#1e2028] transition-colors cursor-pointer ${
                  selectedId === t.id
                    ? 'bg-[#00e87a]/8 border-l-2 border-l-[#00e87a]'
                    : i === 0
                    ? 'bg-[#00e87a]/3 hover:bg-[#00e87a]/6'
                    : 'hover:bg-white/3'
                }`}
              >
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <span className="text-[#3d4149] text-sm font-medium w-5 text-right tabular-nums">{i + 1}</span>
                    <div>
                      <p translate="no" className="text-white text-sm font-semibold">{t.team}</p>
                      <p translate="no" className="text-[#6b7280] text-xs">{t.league}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-center text-[#a8b2c1] text-sm">{t.played}</td>
                {cols.map((c) => (
                  <td key={c.key} className={`px-4 py-4 text-center ${c.highlight ? 'bg-[#00e87a]/3' : ''}`}>
                    {c.cell(t)}
                  </td>
                ))}
                <td className="px-4 py-4">
                  <div className="flex items-center justify-center gap-0.5">
                    {t.form.map((r, j) => <FormBadge key={j} r={r} />)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
